import { Text, View } from "@react-pdf/renderer";
import { Style } from "@react-pdf/types";
import { PDF_EEG_EVALUATION_FONT_SIZE } from "./constants";

export type HtmlNode = {
    tag: string;
    attributes: Record<string, string>;
    children: Array<HtmlNode | string>;
};

const HEADING_FONT_SIZES: Record<string, number> = {
    h2: 16,
    h3: 14,
    h4: 12,
};

const LIST_BULLET = "•";
const LIST_INDENT_PER_LEVEL = 14;
const LIST_LINE_HEIGHT = 1.6;

export const parseHtml = (html: string): Array<HtmlNode | string> => {
    if (!html) return [];
    const doc = new DOMParser().parseFromString(html, "text/html");
    return convertChildNodes(doc.body.childNodes);
};

const convertChildNodes = (childNodes: NodeListOf<ChildNode>): Array<HtmlNode | string> => {
    const nodes: Array<HtmlNode | string> = [];
    childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
            const text = (child.textContent ?? "").replace(/\u00A0/g, " ");
            if (text.trim()) nodes.push(text);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
            const element = child as Element;
            nodes.push({
                tag: element.tagName.toLowerCase(),
                attributes: {},
                children: convertChildNodes(element.childNodes),
            });
        }
    });
    return nodes;
};

const isInlineTag = (tag: string): boolean => {
    return ["strong", "b", "i", "em", "u", "s", "span", "a"].includes(tag);
};

const getInlineStyle = (tag: string): Style => {
    switch (tag) {
        case "strong":
        case "b":
            return { fontWeight: "bold" };
        case "i":
        case "em":
            return { fontStyle: "italic" };
        case "u":
            return { textDecoration: "underline" };
        case "s":
            return { textDecoration: "line-through" };
        default:
            return {};
    }
};

export const extractTextContent = (node: HtmlNode | string): string => {
    if (typeof node === "string") return node;
    // recusive concatnation
    return node.children.map(extractTextContent).join("");
};

const renderInlineChildren = (
    children: Array<HtmlNode | string>,
    inheritedStyle: Style,
    baseFontSize: number,
    key: string,
    textStyle?: Style,
): React.ReactElement => {
    const elements = children.map((child, index) => {
        if (typeof child === "string") {
            return (
                <Text key={`${key}-text-${index}`} style={{ ...inheritedStyle, fontSize: baseFontSize }}>
                    {child}
                </Text>
            );
        }

        if (child.tag === "br") {
            return <Text key={`${key}-br-${index}`}>{"\n"}</Text>;
        }

        if (isInlineTag(child.tag)) {
            const inlineStyle = getInlineStyle(child.tag);
            const mergedStyle = { ...inheritedStyle, ...inlineStyle };
            return renderInlineChildren(child.children, mergedStyle, baseFontSize, `${key}-${child.tag}-${index}`);
        }

        return (
            <Text key={`${key}-fallback-${index}`} style={{ ...inheritedStyle, fontSize: baseFontSize }}>
                {extractTextContent(child)}
            </Text>
        );
    });

    if (elements.length === 1) {
        return elements[0];
    }

    return (
        <Text key={key} style={{ ...inheritedStyle, fontSize: baseFontSize, ...textStyle }}>
            {elements}
        </Text>
    );
};

const renderListItems = (
    node: HtmlNode,
    baseFontSize: number,
    depth: number,
    key: string,
): Array<React.ReactElement> => {
    const isOrdered = node.tag === "ol";
    const elements: Array<React.ReactElement> = [];
    let itemIndex = 0;

    node.children.forEach((child, childIndex) => {
        if (typeof child === "string") return;
        if (child.tag !== "li") return;

        itemIndex++;
        const orderedMarker = depth === 0
            ? `${itemIndex}.`
            : depth === 1
                ? `${String.fromCharCode(96 + itemIndex)}.`
                : LIST_BULLET;
        const marker = isOrdered ? orderedMarker : LIST_BULLET;
        const indent = (depth + 1) * LIST_INDENT_PER_LEVEL;

        const textChildren: Array<HtmlNode | string> = [];
        const nestedLists: Array<HtmlNode> = [];

        child.children.forEach((liChild) => {
            if (typeof liChild === "string") {
                textChildren.push(liChild);
            } else if (liChild.tag === "ul" || liChild.tag === "ol") {
                nestedLists.push(liChild);
            } else {
                textChildren.push(liChild);
            }
        });

        elements.push(
            <View key={`${key}-li-${childIndex}`} wrap={false} style={{ flexDirection: "row", marginLeft: indent, marginBottom: 2 }}>
                <Text style={{ fontSize: baseFontSize, width: 15, lineHeight: LIST_LINE_HEIGHT }}>{marker} </Text>
                <View style={{ flex: 1 }}>
                    {renderInlineChildren(textChildren, { lineHeight: LIST_LINE_HEIGHT }, baseFontSize, `${key}-li-content-${childIndex}`)}
                </View>
            </View>,
        );

        nestedLists.forEach((nestedList, nestedIndex) => {
            const nestedElements = renderListItems(nestedList, baseFontSize, depth + 1, `${key}-nested-${childIndex}-${nestedIndex}`);
            elements.push(...nestedElements);
        });
    });

    return elements;
};

const renderNode = (
    node: HtmlNode | string,
    baseFontSize: number,
    index: number,
    parentKey: string,
    textStyle?: Style,
): React.ReactElement | null => {
    const key = `${parentKey}-${index}`;

    if (typeof node === "string") {
        const trimmed = node.trim();
        if (!trimmed) return null;
        return (
            <Text key={key} style={{ fontSize: baseFontSize, marginBottom: 2, ...textStyle }}>
                {trimmed}
            </Text>
        );
    }

    if (node.tag === "br") {
        return <Text key={key}>{"\n"}</Text>;
    }

    const headingSize = HEADING_FONT_SIZES[node.tag];
    if (headingSize) {
        return (
            <View key={key} style={{ marginBottom: 4, marginTop: 6 }}>
                {renderInlineChildren(node.children, { fontWeight: "bold" }, headingSize, key)}
            </View>
        );
    }

    if (node.tag === "p") {
        const textContent = extractTextContent(node);
        const isBlank = textContent.trim() === "" || textContent.trim() === " ";

        if (isBlank) {
            return <View key={key} style={{ height: baseFontSize }} />;
        }

        return (
            <View key={key}>
                {renderInlineChildren(node.children, { ...textStyle }, baseFontSize, key)}
            </View>
        );
    }

    if (node.tag === "ul" || node.tag === "ol") {
        const listIndent = textStyle && "textIndent" in textStyle ? (textStyle as Record<string, number>).textIndent : 0;
        const listElements = renderListItems(node, baseFontSize, 0, key);
        return (
            <View key={key} style={{ marginBottom: 4, marginLeft: listIndent }}>
                {listElements}
            </View>
        );
    }

    if (isInlineTag(node.tag)) {
        const inlineStyle = getInlineStyle(node.tag);
        return renderInlineChildren(node.children, inlineStyle, baseFontSize, key);
    }

    if (node.tag === "div" || node.tag === "section" || node.tag === "article") {
        const childElements = node.children
            .map((child, childIndex) => renderNode(child, baseFontSize, childIndex, key, textStyle))
            .filter(Boolean);
        return (
            <View key={key}>
                {childElements}
            </View>
        );
    }

    return (
        <Text key={key} style={{ fontSize: baseFontSize, marginBottom: 2, ...textStyle }}>
            {extractTextContent(node)}
        </Text>
    );
};

type HtmlToPdfElementsProps = {
    html: string;
    baseFontSize?: number;
    textStyle?: Style;
};

export const trimTrailingBlankNodes = (nodes: Array<HtmlNode | string>): Array<HtmlNode | string> => {
    let endIndex = nodes.length;
    while (endIndex > 0) {
        const last = nodes[endIndex - 1];
        const isBlankText = typeof last === "string" && last.trim() === "";
        const isBlankParagraph = typeof last !== "string" && last.tag === "p" && extractTextContent(last).trim() === "";
        if (!isBlankText && !isBlankParagraph) break;
        endIndex--;
    }
    return nodes.slice(0, endIndex);
};

export const HtmlToPdfElements = ({ html, baseFontSize = PDF_EEG_EVALUATION_FONT_SIZE.body, textStyle }: HtmlToPdfElementsProps) => {
    const nodes = trimTrailingBlankNodes(parseHtml(html));

    const elements = nodes
        .map((node, index) => renderNode(node, baseFontSize, index, "root", textStyle))
        .filter(Boolean);

    return <View>{elements}</View>;
};
